import React from 'react';
import { BookIcon, TagIcon, CalendarIcon, PackageIcon, PaletteIcon, BriefcaseIcon, PercentIcon, FileTextIcon, UsersIcon, UploadCloudIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { ProductType } from '../../../data/productManagerTypes';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
interface TypeSelectionScreenProps {
  darkMode: boolean;
  onTypeSelect: (type: ProductType) => void;
}
const typeCards: {
  type: ProductType;
  icon: React.ElementType;
  label: string;
  description: string;
  color: string;
  hoverBorder: string;
}[] = [{
  type: 'book',
  icon: BookIcon,
  label: 'Book',
  description: 'Create or edit a book listing with cover, description, and metadata',
  color: 'text-purple-600',
  hoverBorder: 'hover:border-purple-600'
}, {
  type: 'anthology',
  icon: UsersIcon,
  label: 'Anthology',
  description: 'Create a multi-author anthology — invite contributors, set charity donations, and manage stories',
  color: 'text-rose-500',
  hoverBorder: 'hover:border-rose-500'
}, {
  type: 'merch',
  icon: TagIcon,
  label: 'Merchandise',
  description: 'Create or edit merchandise listings like apparel and accessories',
  color: 'text-amber-500',
  hoverBorder: 'hover:border-amber-500'
}, {
  type: 'event',
  icon: CalendarIcon,
  label: 'Event',
  description: 'Create or edit events like signings, Q&As, and workshops',
  color: 'text-teal-500',
  hoverBorder: 'hover:border-teal-500'
}, {
  type: 'bookbox',
  icon: PackageIcon,
  label: 'Book Box',
  description: 'Create or edit subscription boxes or curated book bundles',
  color: 'text-pink-500',
  hoverBorder: 'hover:border-pink-500'
}, {
  type: 'creative',
  icon: PaletteIcon,
  label: 'Creative Services',
  description: 'Set up your creative profile, services, portfolio, and availability',
  color: 'text-violet-500',
  hoverBorder: 'hover:border-violet-500'
}, {
  type: 'service',
  icon: BriefcaseIcon,
  label: 'Service Provider',
  description: 'Set up your service profile for editing, formatting, PA, or other author services',
  color: 'text-blue-500',
  hoverBorder: 'hover:border-blue-500'
}, {
  type: 'sale',
  icon: PercentIcon,
  label: 'Sale / Deal',
  description: 'Post a discount, bundle deal, or limited-time offer — published to The Trove marketplace',
  color: 'text-orange-500',
  hoverBorder: 'hover:border-orange-500'
}, {
  type: 'article',
  icon: FileTextIcon,
  label: 'Article / Blog Post',
  description: 'Write and publish an article or resource — shared directly to The Trove for your community',
  color: 'text-emerald-600',
  hoverBorder: 'hover:border-emerald-600'
}];
export const TypeSelectionScreen: React.FC<TypeSelectionScreenProps> = ({
  onTypeSelect
}) => {
  const dk = useDarkClasses();
  const navigate = useNavigate();
  return <div className={`min-h-screen pt-20 md:pt-8 pb-8 px-4 sm:px-6 lg:px-8 ${dk.page} ${dk.textPrimary}`} data-id="element-22019" data-mp="2d0k81zgqk5ri">
      <div className="max-w-5xl mx-auto" data-id="element-22020" data-mp="2huy6kaqp2t0e">
        <div className="text-center mb-12" data-id="element-22021" data-mp="234h5ishz2541">
          <h1 className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 ${dk.textHeading}`} data-id="element-22022" data-mp="235s9otfbxevo">
            Create or Edit
          </h1>
          <p className={`text-base sm:text-lg ${dk.textSecondary}`} data-id="element-22023" data-mp="2vsczvqk2jkxn">
            What would you like to work on?
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-22024" data-mp="30qy0hr38g6mm">
          <button onClick={() => navigate('/vendors/import-books')} className={`p-6 sm:p-8 rounded-xl border-2 transition-all hover:scale-[1.02] active:scale-[0.98] ${dk.card} hover:border-blue-600`} data-id="element-22025" data-mp="2ltx6ti1he1li">
            <UploadCloudIcon className={`h-8 w-8 mb-4 text-blue-600`} data-id="element-22026" data-mp="1fed2oncly9e3" />
            <h3 className={`text-lg sm:text-xl font-bold mb-2 text-blue-600`} data-id="element-22027" data-mp="1ffo8sttizt8d">
              Import Books
            </h3>
            <p className={`text-xs sm:text-sm ${dk.textMuted}`} data-id="element-22028" data-mp="1vnc5f4muldsz">
              Import your backlist via ISBN, ASIN, or CSV to quickly build your
              catalog
            </p>
          </button>
          {typeCards.map(card => {
          const Icon = card.icon;
          return <button key={card.type} onClick={() => onTypeSelect(card.type)} className={`p-6 sm:p-8 rounded-xl border-2 transition-all hover:scale-[1.02] active:scale-[0.98] ${dk.card} ${card.hoverBorder}`} data-id="element-22029" data-mp="1gusausdrken5">
                <Icon className={`h-8 w-8 mb-4 ${card.color}`} data-id="element-22030" data-mp="24kcsn7y6l5uu" />
                <h3 className={`text-lg sm:text-xl font-bold mb-2 ${card.color}`} data-id="element-22031" data-mp="1znw7mmma37lz">
                  {card.label}
                </h3>
                <p className={`text-xs sm:text-sm ${dk.textMuted}`} data-id="element-22032" data-mp="o7xt438bztdd">
                  {card.description}
                </p>
              </button>;
        })}
        </div>
      </div>
    </div>;
};